import React, { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search } from 'lucide-react';
import { userService } from '../../_services';

function FindUser({ text, closeFindUser }) {
    const [searchText, setSearchText] = useState('');
    const [searchDisplay, setSearchDisplay] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(true);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const handleSearchKeyUp = (e) => {
        const value = e.target.value;
        setSearchText(value);
        if (value.length > 0) {
            setDisabled(false);
        } else {
            setDisabled(true);
            setSearchDisplay([]);
        }
    };

    const getSearchDataList = useCallback(async (stext) => {
        try {
            let searchTerm = stext;
            if (searchTerm.length > 0 && searchTerm.toLowerCase() === 'rxhacker') {
                searchTerm = 'Patient Rx Discount';
            }

            const apidata = await userService.Admin_Any_Search(searchTerm);
            
            if (apidata.length > 0) {
                const headingText = apidata
                    .map(item => item.vHeading)
                    .filter((value, index, self) => self.indexOf(value) === index);
                
                let mainData = [];
                headingText.forEach((heading) => {
                    let filterName = [];
                    const valueText = apidata.filter(a => a.vHeading === heading);
                    valueText.forEach((y) => {
                        filterName.push(y.vName);
                    });
                    mainData.push({ vHeading: heading, vName: filterName });
                });
                
                setSearchDisplay(mainData);
                setError('');
                setLoading(false);
                setDisabled(false);
            } else {
                setSearchDisplay([]);
                setError('No result found');
                setLoading(false);
                setDisabled(false);
            }
        } catch (error) {
            setSearchDisplay([]);
            setError('Oops! Something went wrong. Try Again.');
            setLoading(false);
            setDisabled(false);
        }
    }, []);

    const searchOnButtonClick = (e) => {
        e.preventDefault();
        if (searchText.length >= 0) {
            setLoading(true);
            setDisabled(true);
            getSearchDataList(searchText);
        } else {
            setSearchDisplay([]);
            setError('Input field required');
        }
    };

    // Helper function to parse name string into structured data
    const parseNameString = (nameString) => {
        // Replace 'Patient Rx Discount' with 'RxHacker'
        const cleanedName = nameString.replace('Patient Rx Discount', 'RxHacker');
        
        // Split by pipe character to get segments
        const segments = cleanedName.split('|').map(s => s.trim());
        
        return segments;
    };

    return (
        <Dialog open={true} onOpenChange={closeFindUser}>
            <DialogContent className="max-w-2xl h-[calc(100vh-3rem)]">
                <div className="flex flex-col h-full">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>{text || 'Find User'}</DialogTitle>
                    </DialogHeader>

                    <form onSubmit={searchOnButtonClick} autoComplete="off" className="flex flex-col flex-1 min-h-0">
                        {/* Fixed Search Bar */}
                        <div className="flex-shrink-0 flex gap-2">
                            <Input
                                type="text"
                                name="search"
                                placeholder="Search"
                                value={searchText}
                                onChange={handleSearchKeyUp}
                                className="flex-1"
                            />
                            <Button 
                                type="submit"
                                disabled={disabled || loading}
                                className="bg-primary hover:bg-primary/90"
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <>
                                        <Search className="h-4 w-4 mr-2" />
                                        Search
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Scrollable Results */}
                        {searchDisplay && searchDisplay.length > 0 && (
                            <div className="flex-1 overflow-y-auto min-h-0 mt-4 space-y-4">
                                {searchDisplay.map((item, index) => (
                                    <div 
                                        key={index} 
                                        className="border rounded-lg p-4"
                                    >
                                        <h3 className="font-semibold text-foreground mb-3">{item.vHeading}</h3>
                                        <div className="space-y-2">
                                            {item.vName.map((name, idx) => {
                                                const segments = parseNameString(name);
                                                return (
                                                    <div key={idx} className="flex flex-wrap gap-2 items-start">
                                                        {segments.map((segment, segIdx) => (
                                                            <Badge 
                                                                key={segIdx} 
                                                                variant="secondary"
                                                                className="text-sm font-normal break-all max-w-full"
                                                            >
                                                                {segment}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Error Message */}
                        {error && (
                            <div className="flex-shrink-0 text-sm text-destructive bg-destructive/10 p-3 rounded-md mt-4">
                                {error}
                            </div>
                        )}
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default FindUser;
